import type { BookFontId } from '@historyjki/shared';

// Font TTF imports — Vite resolves these to URLs at build time
import atkinsonRegularUrl from '../assets/fonts/AtkinsonHyperlegible-Regular.ttf?url';
import atkinsonBoldUrl from '../assets/fonts/AtkinsonHyperlegible-Bold.ttf?url';
import andikaRegularUrl from '../assets/fonts/Andika-Regular.ttf?url';
import andikaBoldUrl from '../assets/fonts/Andika-Bold.ttf?url';
import quicksandRegularUrl from '../assets/fonts/Quicksand-Regular.ttf?url';
import quicksandBoldUrl from '../assets/fonts/Quicksand-Bold.ttf?url';
import patrickHandRegularUrl from '../assets/fonts/PatrickHand-Regular.ttf?url';
import loraRegularUrl from '../assets/fonts/Lora-Regular.ttf?url';
import loraBoldUrl from '../assets/fonts/Lora-Bold.ttf?url';
import frauncesRegularUrl from '../assets/fonts/Fraunces-Regular.ttf?url';
import frauncesBoldUrl from '../assets/fonts/Fraunces-Bold.ttf?url';

export interface BookFontEntry {
  id: BookFontId;
  label: string;
  cssFamily: string;
  regularUrl: string;
  boldUrl: string;
}

export const BOOK_FONTS: BookFontEntry[] = [
  { id: 'atkinson-hyperlegible', label: 'Atkinson Hyperlegible', cssFamily: '"Atkinson Hyperlegible", sans-serif', regularUrl: atkinsonRegularUrl, boldUrl: atkinsonBoldUrl },
  { id: 'andika', label: 'Andika', cssFamily: '"Andika", sans-serif', regularUrl: andikaRegularUrl, boldUrl: andikaBoldUrl },
  { id: 'quicksand', label: 'Quicksand', cssFamily: '"Quicksand", sans-serif', regularUrl: quicksandRegularUrl, boldUrl: quicksandBoldUrl },
  { id: 'patrick-hand', label: 'Patrick Hand', cssFamily: '"Patrick Hand", cursive', regularUrl: patrickHandRegularUrl, boldUrl: patrickHandRegularUrl },
  { id: 'lora', label: 'Lora', cssFamily: '"Lora", serif', regularUrl: loraRegularUrl, boldUrl: loraBoldUrl },
  // "Fraunces Book", not "Fraunces": the app UI already loads a VARIABLE
  // Fraunces (opsz 9–144, auto optical sizing) from Google Fonts under the
  // family name "Fraunces" (index.html, --font-display). Registering the
  // book face under the same name makes the compose canvas render/measure
  // with whichever face wins the @font-face cascade — not necessarily the
  // file the PDF embeds. A dedicated family name pins canvas and PDF to
  // the identical font file.
  { id: 'fraunces', label: 'Fraunces', cssFamily: '"Fraunces Book", serif', regularUrl: frauncesRegularUrl, boldUrl: frauncesBoldUrl },
];

let fontsRegistered = false;

export function ensureBookFontsRegistered(): void {
  if (fontsRegistered) return;
  fontsRegistered = true;

  const style = document.createElement('style');
  const rules = BOOK_FONTS.flatMap((f) => [
    `@font-face { font-family: ${f.cssFamily.split(',')[0]}; font-weight: 400; src: url(${f.regularUrl}) format('truetype'); }`,
    `@font-face { font-family: ${f.cssFamily.split(',')[0]}; font-weight: 700; src: url(${f.boldUrl}) format('truetype'); }`,
  ]);
  style.textContent = rules.join('\n');
  document.head.appendChild(style);
}

async function fetchFontBytes(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load font: ${url}`);
  return res.arrayBuffer();
}

export async function loadFontPair(fontId: BookFontId): Promise<{ regular: ArrayBuffer; bold: ArrayBuffer }> {
  const entry = BOOK_FONTS.find((f) => f.id === fontId);
  if (!entry) throw new Error(`Unknown font: ${fontId}`);
  const [regular, bold] = await Promise.all([
    fetchFontBytes(entry.regularUrl),
    fetchFontBytes(entry.boldUrl),
  ]);
  return { regular, bold };
}
